import React, { useState, useEffect } from 'react';
import {
  useLocation,
  useHistory,
} from 'react-router-dom/cjs/react-router-dom.min';
import {
  MainContainer,
  Button,
  CloseImg,
  CountBadge,
  IconImg,
  MenuDivider,
  MenuSectionHeading,
} from './style';
import { useQuery } from '@tanstack/react-query';
import {
  getCommunityTasks,
  getTasks,
  getAssignments,
  getCompletedTasks,
  updateTasksViewedCount,
  updateMarkingToolsViewedCount,
  updateSuccessCriteriasViewedCount,
  updateShortAnswersViewedCount
} from '../../service';
import commentSelected from '../../static/img/commentSelected.svg';
import commentUnSelected from '../../static/img/commentUnSelected.svg';
import markSelected from '../../static/img/markSelected.svg';
import markUnSelected from '../../static/img/markUnSelected.svg';
import closeicon from '../../static/img/closecircle.svg';
import shareIcon from '../../static/img/24grayshare.svg';
import shareIconSelected from '../../static/icons/purple-share-icon.svg';
import markingTemplateSelected from '../../static/img/rubric-outcome.svg';
import markingTemplateUnSelected from '../../static/icons/black-rubric-icon.svg';
import shortAnswerIcon from '../../static/icons/ShortAnsIcon.svg';
import purpleShortAnsIcon from '../../static/icons/PurpleShortAns.svg';
import advancedControlsIcon from '../../static/icons/advanced-controls.svg';
import advancedControlsActiveIcon from '../../static/icons/advanced-controls-active.svg';
import currentTasksIcon from '../../static/icons/current-tasks.svg';
import currentTasksIconSelected from '../../static/icons/current-tasks-selected.svg';
import closedTasksIcon from '../../static/icons/closed-tasks.svg';
import closedTasksIconSelected from '../../static/icons/closed-tasks-selected.svg';
import exemplarsIcon from '../../static/icons/exemplars.svg';
import exemplarsIconSelected from '../../static/icons/exemplars-selected.svg';
import sharedWithMeIcon from '../../static/icons/shared-with-me.svg';
import sharedWithMeIconSelected from '../../static/icons/shared-with-me-selected.svg';
import viewRequestsIcon from '../../static/icons/view-requests.svg';
import viewRequestsIconSelected from '../../static/icons/view-requests-selected.svg';
import feedbackHistoryIcon from '../../static/icons/feedback-history.svg';
import feedbackHistoryIconSelected from '../../static/icons/feedback-history-selected.svg';

import { getUserRole } from '../../userLocalDetails';
import {
  buildTasksMenu,
  isActiveButton,
  isTeacherWithoutClass,
  showSelectedIcon,
  withSectionHeadings,
  replayMenuLabelParts,
} from './rules';
import { isTabletView } from '../ReactiveRender';
import { useAssignmentsAll, useClassData, useTasks, useProfile, useShares } from '../state/hooks';
import { useLexicon } from '../../hooks/useLexicon';
import Loader from '../Loader';
import { canViewCurrentTasks, canViewClosedTasks, canViewFeedbackRequests, canViewSharedResponses, canViewFeedbackHistory, canAccessMarkingTools } from '../../utils/userAccessHelper';
import {
  filterTasksByProgressAndClass,
  getAwaitingSubmissions,
  getDraftTasks,
  getInProgressTasks,
  getTasksWithClasses,
  groupByClass,
} from '../../service/tasks';

const SecondSidebar = ({ id, setShowMenu }) => {
  const { data: profile, isLoadingdata: isLoadingProfile, resetData: resetProfile } = useProfile();
  const { t } = useLexicon();
  const { data: classData, isLoadingdata: isLoadingclassData } = useClassData();
  const [containerHeight, setContainerHeight] = useState(0);
  const [feedbackRequestsLength, setFeedbackRequestsLength] = useState(0);

  const { data: allAssignmentData, isLoadingdata: isAllAssignmentDataLoading } =
    useAssignmentsAll();
  const tasksWithClasses = getTasksWithClasses(allAssignmentData, classData);
  const { data: tasks, isLoadingdata: isLoadingTasks } = useTasks();
  const { data: tasksShares, resetData: resetTasksShares } = useShares('TASK');
  const { data: markingToolsShares, resetData: resetMarkingToolsShares } = useShares(null);
  const assignedTasks = filterTasksByProgressAndClass(tasks, 'ASSIGNED');
  const inProgressTasks = filterTasksByProgressAndClass(tasks, 'DRAFT');
  const inReviewTasks = filterTasksByProgressAndClass(tasks, 'REVIEW');
  const allStudentTasks =
    assignedTasks.length + inProgressTasks.length + inReviewTasks.length;

  const allTeacherTasks =
    getDraftTasks(allAssignmentData).length +
    getAwaitingSubmissions(allAssignmentData).length +
    getInProgressTasks(allAssignmentData).length;

  const location = useLocation();
  const history = useHistory();
  const role = getUserRole();
  const isTeacherNoClass = isTeacherWithoutClass(role, classData);
  const tabletView = isTabletView();

  const completedTasksQuery = useQuery({
    queryKey: ['completedTasks'],
    queryFn: async () => {
      const result = await getCompletedTasks();
      return result;
    },
  });

  const assignmentsQuery = useQuery({
    queryKey: ['assignments'],
    queryFn: async () => {
      const result = await getAssignments();
      return result;
    },
  });

  const communityTasksQuery = useQuery({
    queryKey: ['communityTasks'],
    queryFn: async () => {
      const result = await getCommunityTasks();
      return result;
    },
  });

  useEffect(() => {
    if (communityTasksQuery.data) {
      setFeedbackRequestsLength(communityTasksQuery.data?.totalElements);
    }
  }, [communityTasksQuery.data]);

  useEffect(() => {
    const updateHeight = () => {
      const windowHeight = window.innerHeight;
      setContainerHeight(`calc(${windowHeight}px - 170px)`);
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const handleButtonClick = async (link) => {
    if (link === '/sharedtasks') {
      try {
        await updateTasksViewedCount();
        resetProfile();
        resetTasksShares();
      } catch (error) {
        console.error('Error updating tasks viewed count:', error);
      }
    } else if (link === '/sharedmarkingtools') {
      try {
        await updateMarkingToolsViewedCount();
        await updateSuccessCriteriasViewedCount();
        await updateShortAnswersViewedCount();
        resetProfile();
        resetMarkingToolsShares();
      } catch (error) {
        console.error('Error updating marking tools viewed count:', error);
      }
    }
    
    if (setShowMenu) {
      setShowMenu(false);
    }
    history.push(link);
  };

  const closedTaskCount =
    getUserRole() === 'TEACHER'
      ? groupByClass(
          tasksWithClasses?.filter((task) => task.status === 'CLOSED')
        ).length
      : completedTasksQuery.data?.length;

  const countUnviewedShares = (shares, lastViewedTimestamp) => {
    if (!Array.isArray(shares)) return 0;
    if (!lastViewedTimestamp) return shares.length;
    
    const lastViewedDate = new Date(lastViewedTimestamp);
    return shares.filter((share) => {
      const resource = share.resource;
      const sharedOn = resource?.sharedOn;
      if (!sharedOn) return false;
      const sharedOnDate = new Date(sharedOn);
      return sharedOnDate > lastViewedDate;
    }).length;
  };

  const markingTemplateShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'MARKING_TEMPLATE';
  }) : [];

  const commentBankShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS';
  }) : [];

  const shortAnswerShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'SHORT_ANSWERS';
  }) : [];

  const successCriteriaShares = Array.isArray(markingToolsShares) ? markingToolsShares.filter((share) => {
    const resource = share.resource;
    const resourceType = resource?.itemType;
    return resourceType === 'SUCCESS_CRITERIA';
  }) : [];

  const newSharedTasksCount = countUnviewedShares(Array.isArray(tasksShares) ? tasksShares : [], profile?.lastViewedTimestamps?.tasks);
  const newSharedMarkingToolsCount =
    countUnviewedShares(markingTemplateShares, profile?.lastViewedTimestamps?.markingCriterias) +
    countUnviewedShares(commentBankShares, profile?.lastViewedTimestamps?.commentbanks) +
    countUnviewedShares(successCriteriaShares, profile?.lastViewedTimestamps?.successCriterias) +
    countUnviewedShares(shortAnswerShares, profile?.lastViewedTimestamps?.shortAnswers) +
    countUnviewedShares(successCriteriaShares, profile?.lastViewedTimestamps?.successCriteria);

  const subLinks = [
    {
      replayPublicLabel: 'Current Tasks',
      icon: currentTasksIcon,
      selectedIcon: currentTasksIconSelected,
      title: `Current Tasks ${
        role === 'STUDENT' ? `(${allStudentTasks})` : `(${allTeacherTasks})`
      }`,
      link: '/',
      matchLink: '/',
    },
    {
      replayPublicLabel: 'Current Tasks',
      icon: currentTasksIcon,
      selectedIcon: currentTasksIconSelected,
      title: `Current Tasks ${
        role === 'STUDENT' ? `(${allStudentTasks})` : `(${allTeacherTasks})`
      }`,
      link: '/tasks',
      matchLink: '/tasks',
    },
    {
      replayPublicLabel: 'Closed Tasks',
      icon: closedTasksIcon,
      selectedIcon: closedTasksIconSelected,
      title: `Closed Tasks (${closedTaskCount})`,
      link: `/completed`,
      matchLink: `/completed`,
    },
    {
      replayPublicLabel: role === 'STUDENT' ? 'Help a Friend' : 'View Requests',
      icon: viewRequestsIcon,
      selectedIcon: viewRequestsIconSelected,
      title:
        role === 'STUDENT'
          ? `Help a Friend (${feedbackRequestsLength})`
          : 'View Requests',
      link: '/giveFeedback',
      matchLink: '/giveFeedback',
      ...(role === 'STUDENT' ? {} : { count: feedbackRequestsLength }),
    },
    {
      replayPublicLabel: role === 'STUDENT' ? 'Model Responses' : 'Exemplars',
      icon: exemplarsIcon,
      selectedIcon: exemplarsIconSelected,
      title: role === 'STUDENT' ? `Model Responses` : 'Exemplars',
      link: `/sharedresponses`,
      matchLink: `/sharedresponses`,
    },
    {
      replayPublicLabel: role === 'STUDENT' ? 'Feedback From Me' : 'Feedback History',
      icon: feedbackHistoryIcon,
      selectedIcon: feedbackHistoryIconSelected,
      title: `${role === 'STUDENT' ? `Feedback From Me` : `Feedback History`}`,
      link: `/feedbackHistory`,
      matchLink: `/feedbackHistory`,
    },
    {
      icon: markingTemplateUnSelected,
      selectedIcon: markingTemplateSelected,
      title: 'Rubrics',
      link: '/tools/rubrics',
      matchLink: '/tools/rubrics',
    },
    {
      icon: commentUnSelected,
      selectedIcon: commentSelected,
      title: 'Comment Banks',
      link: '/tools/commentbanks',
      matchLink: '/tools/commentbanks',
    },
    {
      icon: markUnSelected,
      selectedIcon: markSelected,
      title: 'Success Criteria',
      link: '/tools/successcriterias',
      matchLink: '/tools/successcriterias',
    },
    {
      icon: shortAnswerIcon,
      selectedIcon: purpleShortAnsIcon,
      title: 'Short Answers',
      link: '/tools/shortanswers',
      matchLink: '/tools/shortanswers',
    },
    {
      icon: sharedWithMeIcon,
      selectedIcon: sharedWithMeIconSelected,
      replayPublicLabel: 'Shared With Me',
      title: 'Shared With Me',
      link: `/sharedtasks`,
      matchLink: `/sharedtasks`,
      count: newSharedTasksCount,
    },
    {
      icon: markingTemplateUnSelected,
      selectedIcon: markingTemplateSelected,
      title: 'Rubrics',
      link: '/tools/rubrics',
      matchLink: id ? `/tools/rubrics/${id}` : '/tools/rubrics',
    },
    {
      icon: shareIcon,
      selectedIcon: shareIconSelected,
      replayPublicLabel: 'Shared With Me',
      title: 'Shared With Me',
      link: '/sharedmarkingtools',
      matchLink: '/sharedmarkingtools',
      count: newSharedMarkingToolsCount,
    },
    {
      icon: advancedControlsIcon,
      selectedIcon: advancedControlsActiveIcon,
      title: t('Marking Preferences'),
      link: '/tools/advanced-controls',
      matchLink: '/tools/advanced-controls',
    },
  ];

  const getSubLink = (link) => {
    return subLinks.find((subLink) => subLink?.link === link);
  };

  const markingToolsSubLinks = canAccessMarkingTools(profile)
    ? [
        getSubLink('/tools/rubrics'),
        getSubLink('/tools/commentbanks'),
        getSubLink('/tools/successcriterias'),
        getSubLink('/tools/shortanswers'),
        getSubLink('/tools/coursematerials'),
        getSubLink('/sharedmarkingtools'),
        getSubLink('/tools/advanced-controls'),
      ].filter(Boolean)
    : [];

  const canView = (item) =>
    ({
      currentTasks: canViewCurrentTasks(profile),
      closedTasks: canViewClosedTasks(profile),
      sharedResponses: canViewSharedResponses(profile),
      feedbackRequests: canViewFeedbackRequests(profile),
      feedbackHistory: canViewFeedbackHistory(profile),
    })[item];
  const tasksMenu = (currentTasksLink) =>
    buildTasksMenu({
      findLink: getSubLink,
      currentTasksLink,
      isStudent: role === 'STUDENT',
      canView,
    });
  const requestsOnlyMenu = withSectionHeadings([
    {
      heading: 'Student requests',
      items: [
        canViewFeedbackRequests(profile) && getSubLink('/giveFeedback'),
        canViewFeedbackHistory(profile) && getSubLink('/feedbackHistory'),
      ],
    },
  ]);

  const subRoutes = isTeacherNoClass
    ? [
        {
          link: '/giveFeedback',
          subLinks: requestsOnlyMenu,
        },
        {
          link: '/feedbackHistory',
          subLinks: requestsOnlyMenu,
        },
        {
          link: '/',
          subLinks: requestsOnlyMenu,
        },
        {
          link: '/sharedtasks',
          subLinks: requestsOnlyMenu,
        },
        {
          link: '/settings',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics/:markingCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks/:commentBankId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/jeddai',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/sharedmarkingtools',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/coursematerials',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers/:shortAnswerId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias/:successCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/advanced-controls',
          subLinks: markingToolsSubLinks,
        },
      ]
    : [
        {
          link: '/',
          subLinks: tasksMenu('/'),
        },
        {
          link: '/tasks',
          subLinks: tasksMenu('/tasks'),
        },
        {
          link: '/giveFeedback',
          subLinks: tasksMenu('/tasks'),
        },
        {
          link: '/feedbackHistory',
          subLinks: tasksMenu('/tasks'),
        },
        {
          link: '/sharedresponses',
          subLinks: tasksMenu('/tasks'),
        },
        {
          link: '/sharedtasks',
          subLinks: tasksMenu('/tasks'),
        },
        {
          link: '/settings',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/rubrics/:markingCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/commentbanks/:commentBankId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/successcriterias/:successCriteriaId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/shortanswers/:shortAnswerId',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/jeddai',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/sharedmarkingtools',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/tools/advanced-controls',
          subLinks: markingToolsSubLinks,
        },
        {
          link: '/completed',
          subLinks: tasksMenu('/tasks'),
        },
      ];
  if (isLoadingclassData || isAllAssignmentDataLoading || isLoadingTasks || completedTasksQuery.isLoading || isLoadingProfile ) {
    return <Loader />;
  }

  return (
    <MainContainer height={containerHeight}>
      {subRoutes.map((route, idx) => {
        const isMatch = route.link === location.pathname || 
          (route.link.includes(':') && location.pathname.match(new RegExp(route.link.replace(/:[^/]+/g, '[^/]+'))));
        
        if (isMatch) {
          return (
            <React.Fragment key={idx}>
              {tabletView && setShowMenu && (
                <Button onClick={() => setShowMenu(false)}>
                  <CloseImg src={closeicon} />
                </Button>
              )}
              {route.subLinks.map((subLink, subIdx) => {
                if (subLink.heading) {
                  return (
                    <React.Fragment key={`heading-${subLink.heading}`}>
                      {subIdx > 0 && <MenuDivider />}
                      <MenuSectionHeading><span data-dd-privacy="mask-user-input">{subLink.heading}</span></MenuSectionHeading>
                    </React.Fragment>
                  );
                }
                    const isActive = isActiveButton(
                      subLink,
                      location.pathname,
                      isTeacherNoClass
                    );
                const replayLabel = replayMenuLabelParts(subLink);

                return (
                  <Button
                    key={subIdx}
                    onClick={() => handleButtonClick(subLink.link)}
                    active={isActive}
                  >
                    {subLink.icon && (
                      <IconImg
                        data-jeddle-replay-static-icon="true"
                        data-dd-privacy="mask-user-input"
                        src={
                          showSelectedIcon(subLink, location.pathname, isActive)
                            ? subLink.selectedIcon
                            : subLink.icon
                        }
                        alt=""
                      />
                    )}
                    {replayLabel ? (
                      <span>
                        <span data-dd-privacy="mask-user-input">{replayLabel.label}</span>
                        {replayLabel.suffix}
                      </span>
                    ) : (
                      <span data-dd-privacy={['/tools/rubrics', '/tools/commentbanks', '/tools/successcriterias', '/tools/shortanswers', '/tools/coursematerials', '/tools/advanced-controls'].includes(subLink.link) ? 'mask-user-input' : undefined}>{subLink.title}</span>
                    )}
                    {subLink.count !== undefined && subLink.count > 0 && (
                      <CountBadge>{subLink.count}</CountBadge>
                    )}
                  </Button>
                );
              })}
            </React.Fragment>
          );
        } else {
          return null;
        }
      })}
    </MainContainer>
  );
};

export default SecondSidebar;
